<script lang="ts" setup>
	import dayjs from 'dayjs'
	import type { SummaryContext } from '~/components/Shared/summary-section.vue'
	import type { TableColumn } from '~/types'

	defineProps<{
		planDetails: PlanInformationContext | null
	}>()
	const { currencyToNaira } = useCurrency()
	const selectedRecord = ref<PurchaseTransaction | null>(null)
	const isSummaryOpen = shallowRef(false)
	const { plan_id } = useRoute().params

	const searchVal = shallowRef('')

	const cellClass = 'text-xs text-gray-600 font-medium tracking-wide xl:whitespace-nowrap'
	const tableColumns: TableColumn = [
		{
			title: 'S/N',
			dataIndex: 'sn',
			key: 'sn',
			customCell: () => ({ class: 'text-center' }),
			className: cellClass,
			width: '50px'
		},
		{
			title: 'Reference',
			dataIndex: 'name',
			key: 'name',
			className: cellClass
		},
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			className: cellClass
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			className: cellClass
		},
		{
			title: 'Premium',
			dataIndex: 'premium',
			key: 'premium',
			className: cellClass
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			className: cellClass
		},
		{
			title: 'Details',
			dataIndex: 'details',
			key: 'details',
			className: cellClass
		}
	]
	const recordSummary = computed<SummaryContext>(() => ({
		Reference: {
			value: selectedRecord.value?.name,
			key: 'name'
		},
		Type: {
			value: selectedRecord.value?.type,
			key: 'type'
		},
		'Transaction ID': {
			value: selectedRecord.value?.transaction_id,
			key: 'id',
			pClass: 'text-xs'
		},
		Date: {
			value: dayjs(selectedRecord.value?.created_at).format('DD/MM/YYYY'),
			key: 'date'
		},
		Time: {
			value: dayjs(selectedRecord.value?.created_at).format('hh:mm a'),
			key: 'time'
		},
		Channel: {
			value: selectedRecord.value?.source,
			key: 'channel'
		},
		Amount: {
			value: currencyToNaira(selectedRecord.value?.amount || 0),
			key: 'amount'
		},
		Status: {
			value: selectedRecord.value?.status,
			key: 'status'
		}
	}))

	const openTransactionRecord = (record: unknown) => {
		selectedRecord.value = record
		isSummaryOpen.value = true
	}
</script>

<template>
	<div>
		<div class="mb-5">
			<forms-search-field v-model:value="searchVal" />
		</div>

		<shared-table
			:columns="tableColumns"
			:source="`/purchases/${plan_id}/transactions`"
			:filter="{ purchase_id: plan_id as string, search: searchVal }"
		>
			<template #bodyCell="{ column, record, index }">
				<span v-if="column.key === 'sn'">{{ index }}</span>
				<shared-badge
					v-if="column.key === 'status'"
					:text="record.status"
					:pending="record.status === 'pending'"
					:active="record.status === 'successful'"
					:failed="record.status === 'failed'"
				/>
				<span v-if="column.key === 'premium'">
					{{ currencyToNaira(record?.amount) }}
				</span>
				<span v-if="column.key === 'date'">
					{{ dayjs(record?.created_at).format('DD/MM/YYYY') }}
				</span>
				<button v-if="column.key === 'details'" type="button" @click="openTransactionRecord(record)">
					<span class="font-medium text-primary-2">View</span>
				</button>
			</template>
		</shared-table>

		<dialogs v-model:show="isSummaryOpen" modal-class="max-w-[626px] w-full">
			<div class="mb-5 text-center">
				<h2 class="text-2xl font-medium tracking-wider text-gray-800"> Transaction details </h2>
			</div>

			<shared-summary-section :summary="recordSummary" no-default-class>
				<template #value(status)="{ content }">
					<shared-badge
						:text="content.value as string"
						:pending="content.value === 'pending'"
						:active="content.value === 'successful'"
						:failed="content.value === 'failed'"
					/>
				</template>
				<template #value(amount)="{ content }">
					<div>{{ currencyToNaira(content?.value as number) }}</div>
				</template>
			</shared-summary-section>
		</dialogs>
	</div>
</template>
